import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, FileText, ArrowRight, ShieldCheck } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api/customs-fillable`;

/**
 * Customer-facing landing page for a sent customs-form package. Lists the
 * forms the forwarder selected; each opens pre-filled & editable, then
 * print/sign. No auth — the secure token in the URL is the access grant.
 */
export default function CustomsPackagePage() {
  const { token } = useParams();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = "Your Customs Forms";
    axios
      .get(`${API}/package/${token}`)
      .then((res) => setPkg(res.data))
      .catch(() => setError("This link could not be opened. Please contact Europacific."))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <Loader2 className="h-8 w-8 animate-spin text-slate-500" />
      </div>
    );
  }
  if (error || !pkg) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 text-slate-600 p-6 text-center">
        {error || "Package not found."}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 py-10 px-4">
      <div className="max-w-2xl mx-auto" data-testid="customs-package-page">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="h-6 w-6 text-amber-500" />
          <h1 className="text-2xl font-semibold text-slate-900">Customs forms to complete</h1>
        </div>
        <p className="text-sm text-slate-500 mb-6">
          {pkg.customer ? `${pkg.customer} — ` : ""}
          {pkg.pf_number ? `shipment ${pkg.pf_number}. ` : ""}
          Most fields are already filled in. Open each form, check &amp; complete the
          highlighted boxes, then print and sign.
        </p>

        <div className="space-y-3">
          {pkg.forms.map((f) => (
            <Link
              key={f.slug}
              to={`/customs-form/${f.slug}?pkg=${token}`}
              className="flex items-center justify-between bg-white rounded-xl border border-slate-200 px-4 py-4 shadow-sm hover:shadow-md hover:border-amber-300 transition-all group"
              data-testid={`customs-package-form-${f.slug}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="h-9 w-9 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                  <FileText className="h-5 w-5 text-amber-600" />
                </span>
                <span className="font-medium text-slate-800 truncate">{f.title}</span>
              </div>
              <span className="flex items-center gap-1 text-sm text-amber-600 font-medium flex-shrink-0">
                Fill &amp; print
                <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
              </span>
            </Link>
          ))}
        </div>

        <p className="text-center text-xs text-slate-400 mt-8">
          Filling in these forms does not submit them to customs — after printing,
          please sign/stamp where required and return them as instructed.
        </p>
      </div>
    </div>
  );
}
